/**
 * Component definition and registry.
 *
 * Components are typed data containers registered with a unique numeric ID.
 * Data components hold runtime state; tag components are boolean flags with no payload.
 */

import { ComponentType } from '../shared/types.js';
import { MAX_COMPONENT_TYPES } from '../shared/constants.js';

// ─── Component Schema ───────────────────────────────────────────────────────────

export interface ComponentSchema {
  [field: string]: ComponentType;
}

export interface ComponentDef<S extends ComponentSchema = ComponentSchema> {
  readonly id: number;
  readonly name: string;
  readonly schema: S;
  readonly isTag: false;
}

export interface TagDef {
  readonly id: number;
  readonly name: string;
  readonly isTag: true;
}

// ─── Registry ───────────────────────────────────────────────────────────────────

let nextComponentId = 0;
const componentRegistry = new Map<string, ComponentDef | TagDef>();

export function defineComponent<S extends ComponentSchema>(
  name: string,
  schema: S,
): ComponentDef<S> {
  if (componentRegistry.has(name)) {
    throw new Error(`Component "${name}" already defined`);
  }
  if (nextComponentId >= MAX_COMPONENT_TYPES) {
    throw new Error(`Maximum component types (${MAX_COMPONENT_TYPES}) exceeded`);
  }

  const def: ComponentDef<S> = {
    id: nextComponentId++,
    name,
    schema,
    isTag: false,
  };

  componentRegistry.set(name, def);
  return def;
}

export function defineTag(name: string): TagDef {
  if (componentRegistry.has(name)) {
    throw new Error(`Tag "${name}" already defined`);
  }
  if (nextComponentId >= MAX_COMPONENT_TYPES) {
    throw new Error(`Maximum component types (${MAX_COMPONENT_TYPES}) exceeded`);
  }

  const def: TagDef = {
    id: nextComponentId++,
    name,
    isTag: true,
  };

  componentRegistry.set(name, def);
  return def;
}

export function getComponentDef(name: string): ComponentDef | TagDef | undefined {
  return componentRegistry.get(name);
}

/**
 * Iterate every registered component + tag definition.
 *
 * Intended for bookkeeping consumers such as the scene serializer that
 * need to enumerate the full set to ask World which ones each entity
 * carries. Iteration order matches registration order.
 */
export function iterComponentDefs(): IterableIterator<ComponentDef | TagDef> {
  return componentRegistry.values();
}

/**
 * Reset the component registry. Only used in tests.
 */
export function _resetComponentRegistry(): void {
  nextComponentId = 0;
  componentRegistry.clear();
}

// ─── Built-in Components ────────────────────────────────────────────────────────

export const Transform = defineComponent('Transform', {
  position: ComponentType.Vector3,
  rotation: ComponentType.Quaternion,
  scale: ComponentType.Vector3,
});

export const Velocity = defineComponent('Velocity', {
  linear: ComponentType.Vector3,
  angular: ComponentType.Vector3,
});

export const Health = defineComponent('Health', {
  current: ComponentType.F32,
  max: ComponentType.F32,
});

// Tags
export const IsPlayer = defineTag('IsPlayer');
export const IsStatic = defineTag('IsStatic');
export const NeedsSync = defineTag('NeedsSync');
export const IsDisabled = defineTag('IsDisabled');

export { ComponentType as Types };
